import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFinancialPlanStore } from '../store/financialPlanStore'
import { ChevronLeft, ChevronRight, Check } from 'lucide-react'
import { PersonalInfoStep } from '../components/intake/PersonalInfoStep'
import { AccountsStep } from '../components/intake/AccountsStep'
import { IncomeStep } from '../components/intake/IncomeStep'
import { ExpensesStep } from '../components/intake/ExpensesStep'
import { RiskProfileStep } from '../components/intake/RiskProfileStep'
import { ReviewStep } from '../components/intake/ReviewStep'
import { useAuth } from '../providers/AuthProvider'
import { upsertPlan } from '../utils/planApi'
import { submitQuestionnaire } from '../utils/submitQuestionnaire'

const STEPS = [
  { id: 0, name: 'Personal Information', component: PersonalInfoStep },
  { id: 1, name: 'Assets & Accounts', component: AccountsStep },
  { id: 2, name: 'Income Sources', component: IncomeStep },
  { id: 3, name: 'Expenses & Budget', component: ExpensesStep },
  { id: 4, name: 'Risk Profile', component: RiskProfileStep },
  { id: 5, name: 'Review & Submit', component: ReviewStep },
]

export function ClientIntake() {
  const navigate = useNavigate()
  const { currentPlan, setCurrentStep, savePlan } = useFinancialPlanStore()
  const { session, profile } = useAuth()
  const [activeStep, setActiveStep] = useState(currentPlan?.currentStep || 0)
  
  if (!currentPlan) {
    navigate('/')
    return null
  }
  
  // Ensure a DB row exists when intake loads
  async function syncPlan(stepOverride?: number) {
    try {
      if (session && profile && currentPlan) {
        const planForSync = stepOverride != null ? { ...currentPlan, currentStep: stepOverride } : currentPlan
        await upsertPlan({
          id: planForSync.id,
          userId: session.user.id,
          advisorId: profile.advisor_id ?? null,
          plan: planForSync,
          riskProfile: planForSync.riskProfile,
        })
      }
    } catch {
      // ignore sync errors during navigation
    }
  }
  
  const handleNext = () => {
    if (activeStep < STEPS.length - 1) {
      const nextStep = activeStep + 1
      setActiveStep(nextStep)
      setCurrentStep(nextStep)
      savePlan()
      syncPlan(nextStep)
    }
  }
  
  const handlePrevious = () => {
    if (activeStep > 0) {
      const prevStep = activeStep - 1
      setActiveStep(prevStep)
      setCurrentStep(prevStep)
      syncPlan(prevStep)
    }
  }
  
  const handleComplete = async () => {
    savePlan()
    try {
      if (session && profile && currentPlan) {
        await upsertPlan({
          id: currentPlan.id,
          userId: session.user.id,
          advisorId: profile.advisor_id ?? null,
          plan: currentPlan,
          riskProfile: currentPlan.riskProfile,
        })

        if (currentPlan.riskProfile && currentPlan.riskAnswers) {
          await submitQuestionnaire({
            planId: currentPlan.id,
            advisorCode: currentPlan.advisorCode,
            client: { name: currentPlan.clientName, email: currentPlan.email },
            riskAnswers: currentPlan.riskAnswers,
            riskProfile: currentPlan.riskProfile,
          })
          navigate('/thanks')
          return
        }
      }
    } catch (e) {
      // fall back to local navigation if server calls fail
    }
    navigate(`/dashboard/${currentPlan.id}`)
  }
  
  const CurrentStepComponent = STEPS[activeStep].component
  const isLastStep = activeStep === STEPS.length - 1
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Financial Plan Setup
              </h1>
              <p className="text-sm text-gray-600">
                {currentPlan.clientName}
              </p>
            </div>
            <button
              onClick={() => {
                savePlan()
                navigate('/')
              }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Save & Exit
            </button>
          </div>
        </div>
      </header>
      
      {/* Progress Stepper */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <nav aria-label="Progress">
            <ol className="flex items-center justify-between">
              {STEPS.map((step, index) => (
                <li key={step.id} className="relative flex-1">
                  {/* Connector Line */}
                  {index !== 0 && (
                    <div
                      className={`absolute left-0 top-4 -ml-px h-0.5 w-full ${
                        index <= activeStep ? 'bg-primary-600' : 'bg-gray-300'
                      }`}
                      style={{ transform: 'translateY(-50%)' }}
                    />
                  )}
                  
                  <button
                    onClick={() => {
                      if (index < activeStep) {
                        setActiveStep(index)
                        setCurrentStep(index)
                      }
                    }}
                    className={`relative flex flex-col items-center group ${
                      index < activeStep ? 'cursor-pointer' : 'cursor-default'
                    }`}
                  >
                    <span
                      className={`w-8 h-8 flex items-center justify-center rounded-full border-2 transition-colors duration-200 ${
                        index < activeStep
                          ? 'bg-primary-600 border-primary-600'
                          : index === activeStep
                          ? 'bg-white border-primary-600 ring-4 ring-primary-50'
                          : 'bg-white border-gray-300'
                      }`}
                    >
                      {index < activeStep ? (
                        <Check className="w-5 h-5 text-white" />
                      ) : (
                        <span
                          className={`text-sm font-semibold ${
                            index === activeStep ? 'text-primary-600' : 'text-gray-500'
                          }`}
                        >
                          {index + 1}
                        </span>
                      )}
                    </span>
                    <span
                      className={`mt-2 text-xs font-medium ${
                        index <= activeStep ? 'text-gray-900' : 'text-gray-500'
                      } hidden sm:block text-center max-w-[120px]`}
                    >
                      {step.name}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </nav>
        </div>
      </div>
      
      {/* Step Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card">
          <h2 className="text-2xl font-bold mb-6">{STEPS[activeStep].name}</h2>
          
          <CurrentStepComponent />
          
          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handlePrevious}
              disabled={activeStep === 0}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </button>
            
            <div className="text-sm text-gray-600">
              Step {activeStep + 1} of {STEPS.length}
            </div>
            
            {isLastStep ? (
              <button
                onClick={handleComplete}
                className="btn-primary flex items-center"
              >
                Complete Setup
                <Check className="w-4 h-4 ml-1" />
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="btn-primary flex items-center"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
